import React, { useState, useCallback, useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import moment from 'moment'

import EmptyScreen from 'packages/ui/EmptyScreen/EmptyScreen'
import WidgetGlobalPrice from 'components/Statistic/WidgetGlobalPrice'
import WidgetPieChartStatistic from 'components/Statistic/WidgetPieChartStatistic'
import { useRegisterBreadcrumbAction } from 'components/AuthenticatedLayout/Header/components/BreadcrumbActions'

import { useSites } from 'store'
import { useYearRange } from '../Traffic/store'
import MonthlyPurchases, { OPERATION_SALES_MONTHLY } from '../components/MonthlyPurchases'
import Export from './Export'
import Filters from '../components/Filters'
import { convertDistributionToGraph, Distribution, convertPricesToKpis } from 'pages/Stats/helpers'

const PURCHASES_DISTRIBUTION = gql`
  query PurchasesDistribution($createdAtStart: String, $createdAtEnd: String, $offerNames: [String], $siteSfIds: [String!]) {
    distribution: purchasesDistribution(created_at_start: $createdAtStart, created_at_end: $createdAtEnd, offer_names: $offerNames, site_sf_ids: $siteSfIds) {
      monthly_price {
        start_date
        end_date
        count
      }
      monthly_usage {
        start_date
        end_date
        count
      }
      offers {
        name
        count
      }
      sites {
        name
        count
      }
      global_price
      global_refunds
      global_average_price
    }
  }
`

const FIND_PURCHASE_DISTRIBUTION_OFFER_LIST = gql`
  query FindPurchaseDistributionOffers($siteSfIds: [String!]) {
    offerList: findPurchaseDistributionOffers(site_sf_ids: $siteSfIds) {
      name
      count
    }
  }
`

const StatsLoadingSkeleton = () => (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ maxHeight: { lg: '17rem' }, flex: 1 }}>
    <WidgetGlobalPrice loading={true} data={[]} icon="" />
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} sx={{ flex: 4 }}>
      <WidgetPieChartStatistic loading={true} data={[]} />
      <WidgetPieChartStatistic loading={true} data={[]} />
    </Stack>
  </Stack>
)

const Purchases = () => {
  const [filterParams, setFilterParams] = useState({})
  const [selectedOffers, setSelectedOffers] = useState<string[]>([])
  const { t } = useTranslation()
  const [{ selected }] = useSites()
  const [yearRange] = useYearRange()

  const siteSfIds = useMemo(
    () => (selected ? selected.map(({ sf_id }) => sf_id) : []),
    [selected],
  )
  const exportAction = useMemo(() => <Export siteSfIds={siteSfIds} />, [siteSfIds])

  useRegisterBreadcrumbAction(exportAction)

  const handleFilterChange = useCallback((newParams) => {
    setFilterParams(prevParams => ({
      ...prevParams,
      ...newParams
    }))
  }, [])

  const handleOfferChange = useCallback((newOffers: string[]) => {
    setSelectedOffers(newOffers)
    handleFilterChange({ offerNames: newOffers })
  }, [handleFilterChange])

  const { data: distributionData, loading: distributionLoading } = useQuery(PURCHASES_DISTRIBUTION, {
    variables: {
      ...filterParams,
      siteSfIds,
      createdAtStart: moment().year(yearRange).startOf('year').format('DD.MM.YYYY'),
      createdAtEnd: moment().year(yearRange).endOf('year').format('DD.MM.YYYY')
    }
  })

  const { data: offerListData } = useQuery(FIND_PURCHASE_DISTRIBUTION_OFFER_LIST, {
    variables: { siteSfIds }
  })

  const processedData = useMemo((): Distribution => {
    return convertDistributionToGraph(distributionData?.distribution)
  }, [distributionData])

  const hasData = distributionData?.distribution && Object.keys(distributionData.distribution).length > 0 && 
    (processedData.offerDistribution.length > 0 || processedData.siteDistribution.length > 0)

  return (
    <Stack gap={2} flex={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent={'space-between'} alignItems={{ xs: 'stretch', sm: 'flex-start' }} flexWrap='wrap' gap={1}>
        <Filters
          section={'purchases'}
          onOfferChange={handleOfferChange}
          selectedOffers={selectedOffers}
          offerDistribution={offerListData?.offerList}
        />
      </Stack>
      {distributionLoading ? (
        <StatsLoadingSkeleton />
      ) : !hasData ? (
        <EmptyScreen icon="square-poll-vertical">{t('statistics.no_data_filter')}</EmptyScreen>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ maxHeight: { lg: '17rem' }, flex: 1 }}>
            <WidgetGlobalPrice
              data={convertPricesToKpis({ distribution: distributionData?.distribution, t })}
              icon="fa-light fa-qrcode"
            />
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} sx={{ flex: 4 }}>
              {processedData.offerDistribution.length > 0 && (
                <WidgetPieChartStatistic data={processedData.offerDistribution} title={t('statistics.group_by_offer')} />
              )}
              {processedData.siteDistribution.length > 0 && (
                <WidgetPieChartStatistic data={processedData.siteDistribution} title={t('statistics.count_purchases')} />
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ flex: 1 }}>
            <MonthlyPurchases
              query={OPERATION_SALES_MONTHLY}
              title={t('statistics.monthly.sales')}
            />
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default Purchases
